import Avatar from "core/avatar/Avatar";
import InputCheckbox from "core/inputs/input-checkbox/InputCheckbox";
import InputDate from "core/inputs/input-date/InputDate";
import InputRadio from "core/inputs/input-radio/InputRadio";
import InputRadioGroup from "core/inputs/input-radio/InputRadioGroup";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputText from "core/inputs/input-text/InputText";
import InputTextArea from "core/inputs/input-text-area/InputTextArea";
import TableBalancesheet from "core/table/TableBalancesheet";
import PropTypes from "prop-types";
import dotNote from "assets/icons/dot-note.svg";
import { ReactComponent as BlueDotLegendIcon } from "assets/icons/blue-dot.svg";
import { ReactComponent as EllipseLegendIcon } from "assets/icons/ellipse.svg";
import { ReactComponent as WarningLegendIcon } from "assets/icons/warning.svg";

import _ from "lodash";
import {
  BALANCESHEET_CSS_CLASSES,
  BALANCESHEET_DESIGN_WIDTH,
  FIELDS_TYPES,
  LANG,
  MODULES,
  MODULES_TYPES,
  FIELD_LABEL,
  BALANCESHEET_AVERAGES_TYPE,
  BALANCESHEET_SALARY_FIELDS,
  MISSION_BALANCESHEET_TOTAL,
} from "constants/constants";
import { RULES } from "constants/forms";
import { Table } from "core/table/Table";
import { parseCheckboxValue, renderIf } from "./helper";
import Button from "core/buttons/button/Button";
import { FormattedMessage } from "react-intl";
import { onChange } from "./tableContentHelper";
import InputFile from "core/inputs/InputFile";
import colors from "assets/styles/mixins/colors.scss";
import { formatDate } from "utils/formatters";
import Icon from "core/icon/Icon";
import Label from "core/label/Label";
import RangeProgressBar from "core/inputs/RangeProgressBar";
import { useEffect, useRef, useState } from "react";

const normalizeDecimalSeparator = (value) =>
  typeof value === "string" ? value.replace(",", ".") : value;

const getSalaryPercentageFieldId = (field) => {
  const fieldOptions = field?.options ? JSON.parse(field.options) : {};
  const sourceFields = fieldOptions[BALANCESHEET_SALARY_FIELDS.SOURCE_FIELDS]
    ?.split(",")
    .map(Number);
  const percentageFieldId = sourceFields?.[1];

  if (
    field?.type !== FIELDS_TYPES.NUMBER ||
    !fieldOptions.class ||
    percentageFieldId == null ||
    Number(field?.field_id) !== percentageFieldId
  ) {
    return null;
  }

  return String(field.field_id);
};

const normalizeAnnualBalancesheetValueForApi = ({
  fieldId,
  value,
  salaryPercentageFieldIds,
}) =>
  salaryPercentageFieldIds.has(String(fieldId))
    ? normalizeDecimalSeparator(value?.trim?.() ?? value)
    : value;

export const parseNumberWithDecimalSeparator = (value) => {
  if (value == null || value === "") return Number.NaN;

  const normalizedValue = normalizeDecimalSeparator(String(value).trim());

  return normalizedValue === ""
    ? Number.NaN
    : Number.parseFloat(normalizedValue);
};

export const MANAGER_PUBLISH_SUCCESS_MODAL_MESSAGES = {
  successTitleId: "annual_balancesheet_manager_validation_modal_title",
  successTitleDefault: "Validation du bilan annuel",
  successMessageId: "annual_balancesheet_manager_validation_modal_message",
  successMessageDefault: "Le bilan annuel a bien été validé.",
};

const SINGLE_VALUE_FIELD_TYPES = new Set([
  FIELDS_TYPES.TEXT,
  FIELDS_TYPES.DATE,
  FIELDS_TYPES.NUMBER,
  FIELDS_TYPES.LABEL,
  FIELDS_TYPES.ENTITY,
  FIELDS_TYPES.CHOICE,
  FIELDS_TYPES.INTEGER,
]);

export const safeJsonParse = (value, fallback = {}) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const excludeHiddenFields = (fields = []) =>
  fields.filter((field) => field?.type !== FIELDS_TYPES.HIDDEN);

export const getBalancesheetModuleClassNames = ({ module }) => {
  const { type, fields = [] } = module || {};
  const classes = ["balancesheet-module-wrapper"];

  const displayableFields = excludeHiddenFields(fields);
  const isEvaluationHistoryModule =
    type === MODULES_TYPES.BASIC &&
    displayableFields.length > 0 &&
    displayableFields.every((field) => SINGLE_VALUE_FIELD_TYPES.has(field?.type));

  if (isEvaluationHistoryModule) {
    classes.push("balancesheet-module--evaluation-history");
  }

  return classes.join(" ");
};

//This function allows to flatten all the modules into a list of modules
export const flattenTree = (node, parent = null) => {
  const result = [{ ...node, parent }];

  if (node.modules) {
    node.modules.forEach((child) => {
      result.push(...flattenTree(child, node.id));
    });
  }

  return result;
};
export const EvaluationComplete = ({
  icon,
  consultantName,
  title,
  message,
}) => {
  return (
    <div className="evaluation-complete-section">
      <div className="evaluation-content">
        <div
          style={{
            backgroundImage: `url(${dotNote})`,
          }}
          className="evaluation-icon"
        ></div>

        <div className="consultant-name">{consultantName}</div>
        <div className="evaluation-title">{title}</div>
        <p className="evaluation-message">{message}</p>
      </div>
    </div>
  );
};

EvaluationComplete.propTypes = {
  icon: PropTypes.string,
  consultantName: PropTypes.string,
  title: PropTypes.string,
  message: PropTypes.string,
  onClose: PropTypes.func,
};

export const renderButton = ({
  color,
  className,
  onClick,
  id,
  messageId,
  defaultMessage,
  disabled,
}) => (
  <Button
    color={color}
    className={className}
    onClick={onClick}
    type="button"
    id={id}
    disabled={disabled}
  >
    <FormattedMessage id={messageId} defaultMessage={defaultMessage} />
  </Button>
);

//example: initial Module name : EVALUE TON MANAGER => formatted module name : evaluetonmanager
export const formatModuleName = (name) =>
  _.chain(name)
    .toLower()
    .replace(/[^a-zA-Z0-9]/g, "")
    .replace(/s+/g, "")
    .value();

export const getFieldValueById = (values, fieldId, lineId) => {
  // case with lineId
  if (lineId) {
    const suffixId = `${fieldId}_${lineId}`;
    const fieldWithLineId = values?.find(
      (item) => item.field_id === suffixId || item.id === suffixId
    );
    if (fieldWithLineId) return fieldWithLineId.value;
  }

  // case without lineId
  const field = values?.find(
    (item) => item.field_id === fieldId || item.id === fieldId
  );
  return field ? field.value : null;
};
export const displayField = ({
  field,
  onChange,
  selectionColor,
  values,
  errors,
  lineId = 1,
  readOnly,
  inputStyle,
  fieldStyle,
  labelValue,
  dynamicHeight,
  optionsForChoice,
  dashOption,
  fileFieldConfig,
  missionId,
  isExport,
  multilineReadOnly,
}) => {
  const options = field?.options ? JSON.parse(field?.options)?.values : [];
  const isUnderlined = JSON.parse(field?.options)?.isUnderlined;
  const err = errors?.[`${field?.field_id}_1`] || null;
  const additionalMsg = err?.type === "minLength" ? err.message : undefined;
  const minLen = getMinLength(field);
  const hasCategoryTag = JSON.parse(field?.options)?.hasCategoryTag;
  const showDatepicker = JSON.parse(field?.options)?.showDatepicker;
  const fieldClass = JSON.parse(field?.options)?.class;

  const getUnderlineStyle = ({
    isUnderlined,
    value,
    dashValue = dashOption,
  }) => {
    if (value === dashValue) {
      return false;
    }
    return isUnderlined;
  };
  const label = `${field?.label || ""} ${renderIf(
    field?.afterfield_label,
    `(${field?.afterfield_label})`,
    ""
  )} `;
  const id = `${field.field_id}_${lineId ?? 1}`;

  switch (field?.type) {
    case FIELDS_TYPES.TEXT:
      return (
        <InputText
          id={id}
          key={id}
          label={_.trim(label)}
          onChange={(e) => {
            onChange({
              id: id,
              field_id: field.field_id,
              value: e.target.value,
            });
          }}
          value={
            getFieldValueById(values, field.field_id, lineId ?? 1) ||
            getDefaultValue({ values, id: id })
          }
          errors={errors?.[id] || null}
          readOnly={readOnly}
          required={field.required}
          inputStyle={inputStyle}
          placeholder={field.default || ""}
          // free text (e.g. N-1 commitments) must wrap instead of being truncated
          multilineReadOnly
        />
      );
    case FIELDS_TYPES.IMAGE:
      return (
        <Avatar
          background={JSON.parse(field?.options)?.src}
          size={"large"}
          isRounded={false}
          id={id}
          required={field.required}
          avatarBackgroundColor={"transparent"}
        />
      );
    case FIELDS_TYPES.INLINE_RADIO: {
      const inlineRadioId = missionId
        ? `${field?.field_id}_${missionId}`
        : `${field?.field_id}_${lineId ?? 1}`;
      const inlineRadioValue =
        (missionId
          ? getFieldValueById(values, inlineRadioId)
          : getFieldValueById(values, field?.field_id, lineId)) ||
        field?.default ||
        options[0]?.val;

      return (
        <InputRadioGroup
          key={`${inlineRadioId}-${inlineRadioValue ?? ""}`}
          label={_.trim(label)}
          required={field.required}
          isInline
          options={options.map((elm) =>
            renderIf(elm?.val, { ...elm, val: elm.val }, elm)
          )}
          id={inlineRadioId}
          getSelectedValue={(e) => {
            onChange({
              id: inlineRadioId,
              field_id: field.field_id,
              value: e.target.value,
            });
          }}
          value={inlineRadioValue}
          errors={getFieldError({ errors, id: inlineRadioId })}
          readOnly={readOnly}
          selectionColor={selectionColor}
        />
      );
    }
    case FIELDS_TYPES.VERTICAL_RADIO:
      return (
        <InputRadioGroup
          required={field.required}
          label={_.trim(label)}
          isInline={false}
          options={options.map((elm) =>
            renderIf(elm?.val, { ...elm, val: elm.val }, elm)
          )}
          id={
            missionId
              ? `${field?.field_id}_${lineId}_${missionId}`
              : renderIf(
                  lineId,
                  `${field?.field_id}_${lineId}`,
                  field?.field_id
                )
          }
          getSelectedValue={(e) => {
            onChange({
              id: missionId
                ? `${field?.field_id}_${missionId}`
                : field?.field_id,
              field_id: field?.field_id,
              value: e.target.value,
            });
          }}
          value={
            _.get(values, `${field?.field_id}_${lineId}`, null) ||
            _.toString(
              getDefaultValue({ values, id: `${field?.field_id}_1` })
            ) ||
            options[0]?.val
          }
          errors={getFieldError({ errors, id })}
          readOnly={readOnly}
          selectionColor={selectionColor}
        />
      );
    case FIELDS_TYPES.TEXT_AREA: {
      const hasRowGroup = field.options && JSON.parse(field.options)?.row_group;

      const { lineId } = parseFieldIdAndLine(id);
      return (
        <InputTextArea
          required={field.required}
          id={id}
          key={id}
          label={_.trim(field.label)}
          onChange={(e) => {
            onChange({
              id: field.id,
              field_id: field.field_id,
              value: e.target.value,
            });
          }}
          width="100%"
          value={
            getFieldValueById(values, field.id, lineId) ||
            getDefaultValue({ values, id })
          }
          inputStyle={inputStyle}
          errors={errors?.[id] || null}
          readOnly={readOnly}
          additionalErrorMessage={additionalMsg}
          additionalErrorValue={String(minLen)}
          placeholder={field.default?.toString()}
          dynamicHeight={dynamicHeight}
          className={hasRowGroup ? "has-row-group" : ""}
          isExport={isExport}
        />
      );
    }

    case FIELDS_TYPES.DATE:
      return (
        <InputDate
          required={field.required}
          id={`${field?.field_id}_${lineId}`}
          label={_.trim(label)}
          onChange={(e) => {
            onChange({
              field_id: field.field_id,
              value: e?.target?.value || e,
            });
          }}
          value={
            getFieldValueById(values, field?.field_id, lineId) ||
            getDefaultValue({ values, id })
          }
          width="100%"
          errors={getFieldError({ errors, id })}
          readOnly={readOnly}
          lang={LANG.FR}
          showDatepicker={showDatepicker}
          enableBoldWhenFilled={true}
        />
      );
    case FIELDS_TYPES.NUMBER:
      return (
        <InputText
          required={field.required}
          id={id}
          label={_.trim(label)}
          onChange={(e) => {
            onChange({
              field_id: field.field_id,
              value: e.target.value,
            });
          }}
          value={
            getFieldValueById(values, field.field_id, lineId) ||
            getDefaultValue({ values, id }) ||
            field?.default
          }
          errors={errors?.[id] || null}
          pattern={RULES.positive_float_pattern}
          readOnly={readOnly}
          afterFieldLabel={field?.afterfield_label}
          inputStyle={inputStyle}
          multilineReadOnly={multilineReadOnly}
        />
      );
    case FIELDS_TYPES.INTEGER:
      return (
        <InputText
          required={field.required}
          id={id}
          label={_.trim(label)}
          pattern={RULES.positive_max_9999}
          onChange={(e) => {
            onChange({
              field_id: field.field_id,
              value: Number(e.target.value),
            });
          }}
          value={field.value ? `${field.value} mois` : `${field.default} mois`}
          errors={getFieldError({ errors, id })}
          readOnly={readOnly}
          inputStyle={inputStyle}
          multilineReadOnly={multilineReadOnly}
        />
      );
    case FIELDS_TYPES.CHOICE: {
      const currentUnderlineStyle = getUnderlineStyle({
        isUnderlined,
        value:
          getFieldValueById(values, field.field_id) ||
          field.default ||
          dashOption,
      });
      return (
        <InputSelect
          required={field.required}
          id={id}
          label={_.trim(label)}
          name={field?.field_id}
          options={(optionsForChoice || options).map((elm) => ({
            value: elm?.val,
            label: elm?.lb,
          }))}
          setValue={(id, e) => {
            onChange({ field_id: id, value: e });
          }}
          value={getFieldValueById(values, id) || field.default || dashOption}
          errors={getFieldError({ errors, id })}
          readOnly={readOnly}
          isUnderlined={currentUnderlineStyle}
          inputStyle={inputStyle}
          isExport={isExport}
        />
      );
    }

    case FIELDS_TYPES.LABEL:
      return (
        <div className="field-container" key={id}>
          <InputText
            required={field.required}
            id={id}
            label={_.trim(label)}
            onChange={(e) => {
              onChange({
                field_id: field.field_id,
                value: e.target.value || e,
              });
            }}
            value={
              getFieldValueById(values, field?.field_id, lineId) ??
              field.default ??
              labelValue
            }
            errors={getFieldError({ errors, id })}
            readOnly={true}
            placeholder={field.default?.toString()}
            hasCategoryTag={hasCategoryTag}
            customLabelFieldStyle={fieldClass}
            inputStyle={inputStyle}
            // read-only copies (e.g. N-1 commitments) must wrap instead of being truncated
            multilineReadOnly
          />
        </div>
      );
    case FIELDS_TYPES.ENTITY:
      return (
        <InputSelect
          required={field.required}
          id={id}
          label={_.trim(label)}
          name={field?.field_id}
          options={options?.map((elm) => ({
            value: elm?.val,
            label: elm?.lb,
          }))}
          setValue={(id, e) => {
            onChange({ field_id: id, value: e });
          }}
          value={
            _.get(values, `${field?.field_id}_${lineId}`, null) ||
            getDefaultValue({ values, id: `${field?.field_id}_1` })
          }
          errors={getFieldError({ errors, id })}
          readOnly={readOnly}
          hasCategoryTag={true}
          isExport={isExport}
        />
      );
    case FIELDS_TYPES.VERTICAL_CHECK: {
      const { lineId: vcLineId } = parseFieldIdAndLine(id);
      return (
        <InputCheckbox
          required={field.required}
          label={_.trim(label)}
          isInline={false}
          options={JSON.parse(field?.options)?.values || []}
          getSelectedValue={(selectedValue) => {
            onChange({
              id: id,
              field_id: field?.field_id,
              value: `${JSON.stringify(selectedValue || [])}`,
            });
          }}
          value={parseCheckboxValue(
            getFieldValueById(values, field.field_id, vcLineId) ||
              getDefaultValue({ values, id }) ||
              "[]"
          )}
          errors={getFieldError({ errors, id })}
          readOnly={readOnly}
        />
      );
    }
    case FIELDS_TYPES.INLINE_CHECK:
      return (
        <InputCheckbox
          required={field.required}
          label={_.trim(label)}
          options={JSON.parse(field?.options)?.values || []}
          getSelectedValue={(selectedValue) => {
            onChange({
              field_id: field?.field_id,
              value: `${JSON.stringify(selectedValue || [])}`,
            });
          }}
          value={
            _.get(values, `${field?.field_id}_${lineId}`, null) ||
            JSON.parse(
              getDefaultValue({ values, id: `${field?.field_id}_1` }) || "[]"
            )
          }
          errors={getFieldError({ errors, id })}
          readOnly={readOnly}
          isInline={true}
        />
      );
    case FIELDS_TYPES.FILE:
      return (
        <InputFile
          icon={fileFieldConfig?.icon || "folder"}
          uploadIcon={fileFieldConfig?.uploadIcon}
          isBottomIconClickable={fileFieldConfig?.isBottomIconClickable}
          required={field.required}
          readOnly={readOnly || fileFieldConfig?.isUploading}
          accept={fileFieldConfig?.accept}
          allowDelete={fileFieldConfig?.allowDelete}
          previewUrl={
            fileFieldConfig?.getFilePreviewUrl?.({
              field,
              lineId,
              moduleFields: fileFieldConfig?.moduleFields,
              values,
            }) || ""
          }
          onPreview={
            fileFieldConfig?.onFilePreview
              ? () =>
                  fileFieldConfig.onFilePreview({
                    field,
                    lineId,
                    moduleFields: fileFieldConfig?.moduleFields,
                    moduleId: fileFieldConfig?.moduleId,
                  })
              : undefined
          }
          onChange={(file) => {
            if (fileFieldConfig?.onFileUpload) {
              fileFieldConfig.onFileUpload({
                field,
                file,
                lineId,
                moduleFields: fileFieldConfig?.moduleFields,
                moduleId: fileFieldConfig?.moduleId,
              });
              return;
            }
            onChange({
              id: field.id,
              field_id: field.field_id,
              value: file.name,
            });
          }}
          onDelete={() => {
            if (fileFieldConfig?.onFileDelete) {
              fileFieldConfig.onFileDelete({
                field,
                lineId,
                moduleFields: fileFieldConfig?.moduleFields,
                moduleId: fileFieldConfig?.moduleId,
              });
              return;
            }
            onChange({ id: field.id, field_id: field.field_id, value: "" });
          }}
          value={getFieldValueById(values, field.field_id, lineId) || ""}
        />
      );
    case FIELDS_TYPES.RANGE:
      return (
        <RangeProgressBar
          id={`${field?.field_id}_1`}
          min={1}
          max={JSON.parse(field.options).values.length}
          step={1}
          value={getFieldValueById(values, field.field_id)}
          onChange={(value) => {
            onChange({ field_id: field.field_id, id: field.id, value });
          }}
          options={JSON.parse(field.options)}
          readOnly={readOnly}
        />
      );
    default:
      return (
        <>
          {field?.label || values?.id}
          <br />
        </>
      );
  }
};

//calculates the SUM of Module list
export const calculateSum = (values) =>
  _.chain(Object.keys(values))
    .reduce((prev, curr) => (prev = prev + values[curr]), 0)
    .value();

//get header of the table for module list
const formatHeaderList = ({ structure }) => {
  if (structure !== "") {
    const header = Object.keys(structure.cols).reduce(
      (prev, curr) => [
        ...prev,
        {
          label: {
            key: curr,
            value: _.get(structure, `subheaders.${curr}`, ""),
          },
          icon: _.get(structure, `cols.${curr}.icon`, null),
          width: _.get(structure, `cols.${curr}.w`),
          marginTop: _.get(structure, `cols.${curr}.marginTop`),
          iconHeight: _.get(structure, `cols.${curr}.height`, 17),
        },
      ],
      []
    );
    return header;
  }
  return [];
};

// format list for module type: LIST
const formatContentList = ({
  fields,
  getSelectedValue,
  moduleName,
  getActiveField,
  onChange,
  values,
  readOnly,
}) => {
  return fields.reduce((prev, curr) => {
    const result = JSON.parse(curr?.options);

    const options = result?.values.reduce((prev1, curr1) => {
      const isSelected = values?.some(
        (elm) =>
          (elm?.field_id ?? elm?.id) === curr?.field_id &&
          elm?.value === curr1?.val
      );

      return {
        ...prev1,
        [_.indexOf(result?.values, curr1) + 2]: {
          id: `${moduleName}_${curr.position}_${curr1?.val}`,
          isSelected,
          isRadioCell: true,
          display: (
            <InputRadio
              key={curr1?.val}
              option={curr1}
              name={curr?.label}
              getSelectedValue={(val) => {
                onChange({ field_id: curr?.field_id, value: val?.value });
                getActiveField(`${moduleName}_${curr.position}_${curr1?.val}`);
                getSelectedValue({
                  fieldClass: result?.fieldClass,
                  val,
                  rval: curr1?.rval,
                });
              }}
              onCellClick={() => {
                onChange({ field_id: curr?.field_id, value: curr1?.val });
                getActiveField(`${moduleName}_${curr.position}_${curr1?.val}`);
                getSelectedValue({
                  fieldClass: result?.fieldClass,
                  val: { value: curr1?.val },
                  rval: curr1?.rval,
                });
              }}
              isChecked={!!isSelected}
              readOnly={readOnly}
            />
          ),
        },
      };
    }, {});

    return [
      ...prev,
      {
        1: <div className="input-list-label">{curr?.label}</div>,
        ...options,
      },
    ];
  }, []);
};

// format list for module type: MR_IC_MODULE
const formatContentListIC = ({ fields, onChange, values, readOnly }) =>
  fields.reduce(
    (prev, curr) => {
      const result = JSON.parse(curr?.options);
      const options = result?.values.reduce(
        (prev1, curr1) => ({
          ...prev1,
          [_.indexOf(result?.values, curr1) + 2]: {
            id: curr?.id,
            isRadioCell: true,
            display: (
              <InputRadio
                key={curr1?.val}
                option={curr1}
                name={curr?.label}
                getSelectedValue={(val) => {
                  onChange({ field_id: curr?.id, value: val?.value });
                }}
                onCellClick={() => {
                  onChange({ field_id: curr?.id, value: curr1?.val });
                }}
                isChecked={
                  !_.chain(values)
                    .filter(
                      (elm) => elm?.id === curr?.id && elm?.value === curr1.val
                    )
                    .first()
                    .isNil()
                    .value()
                }
                readOnly={readOnly}
              />
            ),
          },
        }),
        {}
      );
      return [
        ...prev,
        {
          1: <div className="input-list-label">{curr?.label}</div>,
          ...options,
        },
      ];
    },

    []
  );

// format list for module type:MR_MISSION_MODULE
const formatContentListMission = ({
  fields,
  moduleName,
  getActiveField,
  onChange,
  values,
  readOnly,
}) =>
  fields.reduce(
    (prev, curr) => {
      const result = JSON.parse(curr?.options);
      const hasOriginalProperty = result?.values?.some(
        (value) => "original" in value
      );
      const options = result?.values.reduce((prev1, curr1) => {
        const optionId = `${moduleName}_${curr.position}_${curr1?.val}`;
        const radioName = _.chain(curr?.label)
          .lowerCase()
          .split(" ")
          .join("_")
          .value();
        const uniqueFieldId = curr.id || curr.field_id;
        const radioFormattedId = `${uniqueFieldId}_${radioName}_${curr1?.val}`;
        const isSelected = values.some(
          (elm) => elm.id === uniqueFieldId && elm.value === curr1.val
        );
        return {
          ...prev1,
          [_.indexOf(result?.values, curr1) + 2]: {
            id: radioFormattedId,
            isSelected,
            isRadioCell: true,
            hasOriginalInOptions: hasOriginalProperty,
            original: hasOriginalProperty ? curr1.original : undefined,
            option: curr1,
            display: (
              <InputRadio
                key={`${uniqueFieldId}_${curr1?.val}`}
                option={{ ...curr1, isSelected }}
                name={uniqueFieldId}
                getSelectedValue={(val) => {
                  onChange({
                    id: uniqueFieldId,
                    field_id: curr?.field_id,
                    value: val?.value,
                  });
                  getActiveField(radioFormattedId);
                }}
                onCellClick={() => {
                  onChange({
                    id: uniqueFieldId,
                    field_id: curr?.field_id,
                    value: curr1?.val,
                  });
                  getActiveField(optionId);
                }}
                isChecked={isSelected}
                readOnly={readOnly}
              />
            ),
          },
        };
      }, {});
      return [
        ...prev,
        {
          1: <div className="input-list-label">{curr?.label}</div>,
          ...options,
        },
      ];
    },

    []
  );

// format header for dynamic table
const formatHeaderTable = ({ structure, isEditable, readOnly, isExport }) => {
  if (structure !== "") {
    let header = Object.keys(structure?.cols || {}).reduce(
      (prev, curr) => [
        ...prev,
        {
          value: _.get(structure, `cols.${curr}.lb`, ""),
          width: _.get(structure, `cols.${curr}.w`),
          icon: _.get(structure, `cols.${curr}.icon`),
        },
      ],

      []
    );
    if (isEditable && !readOnly && !isExport) {
      header.push({
        value: "Action",
        width: "15%",
        icon: "trash-2",
      });
    }
    return header;
  }
  return [];
};

const getOrderedTableFieldIds = (fields = []) => [
  ...new Set(
    _.sortBy(fields, ["position", "field_id"]).map((field) =>
      String(field.field_id)
    )
  ),
];

//initialize first row for dynamic table
const initFirstRowValues = (fields) =>
  fields.reduce(
    (prev, curr) => ({
      ...prev,
      [curr.field_id]: getCorrespondValue({ field: curr }),
    }),
    {}
  );

export const formatListLabel = (name) =>
  _.chain(name).lowerCase().split(" ").join("_").value();

const renderMissionBlockFields = (
  fields,
  onChange,
  activeFields,
  getActiveField,
  moduleName,
  values,
  errors,
  readOnly
) => {
  const sortedFields = _.sortBy(fields, "position").filter(
    (field) =>
      field.type === FIELDS_TYPES.INTEGER ||
      field.type === FIELDS_TYPES.LABEL ||
      field.type === FIELDS_TYPES.DATE
  );
  return (
    <div className="mission-block-fields">
      {sortedFields.map((field) => {
        const dynamicStyleString = field?.options
          ? JSON.parse(field?.options)?.style || ""
          : "";
        const fieldStyle = transformDynamicStyle(dynamicStyleString);
        const { lineId } = parseFieldIdAndLine(field?.id);

        return (
          <div className="cell-padding" key={field.id || field.field_id}>
            {displayField({
              field,
              onChange,
              activeFields,
              getActiveField,
              moduleName,
              values,
              errors,
              readOnly: true,
              disableCalendar: true,
              inputStyle: fieldStyle,
              lineId,
            })}
          </div>
        );
      })}
    </div>
  );
};

const renderNameAndMissionDatesInputs = ({
  fields,
  onChange,
  values,
  errors,
  readOnly,
}) => {
  const sortedFields = _.sortBy(fields, "position").filter(
    (field) =>
      field.type === FIELDS_TYPES.LABEL ||
      field.type === FIELDS_TYPES.DATE ||
      field.type === FIELDS_TYPES.TEXT
  );
  return sortedFields.map((field) => {
    const { lineId } = parseFieldIdAndLine(field?.id);

    return (
      <div className="cell-padding" key={field.id || field.field_id}>
        {displayField({
          field,
          onChange,
          values,
          errors,
          readOnly,
          lineId,
        })}
      </div>
    );
  });
};

const parseStructure = (structure) => {
  let formattedStructure;
  try {
    formattedStructure = JSON.parse(structure);
  } catch (error) {
    console.error("error parsing structure", structure, error);
  }
  return formattedStructure;
};

const renderTable = ({
  fields,
  values,
  onChange,
  readOnly,
  activeFields,
  getActiveField,
  getSelectedValue,
  moduleName,
  header,
  isExport,
}) => (
  <Table
    id={"mission-table"}
    header={header}
    content={
      formatContentList({
        fields,
        activeFields,
        getSelectedValue,
        moduleName,
        getActiveField,
        onChange,
        readOnly,
        values,
        isExport,
      }) || []
    }
    activeFields={activeFields}
    selectedColor={colors.gray2}
  />
);

const RenderTbaleBalancesheet = ({
  fields,
  isEditable,
  formattedStructure,
  readOnly,
  dynamicTable,
  onChangeTableValues,
  values,
  disableCalendar,
  rows,
  setRows,
  moduleId,
  isValidated,
  errors,
  isExport,
}) => {
  const orderedTableFieldIds = getOrderedTableFieldIds(fields);

  return (
    <>
      <TableBalancesheet
        isEditable={isEditable}
        tableHeader={formatHeaderTable({
          structure: formattedStructure,
          isEditable,
          readOnly,
          isExport,
        })}
        fields={fields}
        firstRowValues={initFirstRowValues(fields)}
        columnFieldIds={orderedTableFieldIds}
        dynamicTable={dynamicTable}
        onChange={onChangeTableValues}
        readOnly={readOnly}
        values={values}
        formattedStructure={formattedStructure}
        disableCalendar={disableCalendar}
        rows={rows}
        setRows={setRows}
        moduleId={moduleId}
        isValidated={isValidated}
        errors={errors}
        isExport={isExport}
      />
      <br />
    </>
  );
};

RenderTbaleBalancesheet.propTypes = {
  fields: PropTypes.array,
  isEditable: PropTypes.bool,
  formattedStructure: PropTypes.object,
  readOnly: PropTypes.bool,
  dynamicTable: PropTypes.bool,
  onChangeTableValues: PropTypes.func,
  values: PropTypes.object,
  disableCalendar: PropTypes.bool,
  rows: PropTypes.array,
  setRows: PropTypes.func,
  moduleId: PropTypes.string,
  isValidated: PropTypes.bool,
  errors: PropTypes.object,
  isExport: PropTypes.bool,
};

const getIntegerField = (fields) =>
  fields.find((elm) => elm.type === FIELDS_TYPES.INTEGER);

const getInlineRadioFields = (fields) =>
  fields.filter((elm) => elm.type === FIELDS_TYPES.INLINE_RADIO);

const hasScoreProperty = (obj, fieldName) =>
  Object.prototype.hasOwnProperty.call(obj || {}, fieldName);

const isScoreKeyForMission = (scoreStorageKey, missionId) =>
  scoreStorageKey.startsWith(`${missionId}_`);

// Annual balancesheets store one scored mission block per mission.
const findMissionScoreStorageKey = (scores, missionId) =>
  Object.keys(scores).find(
    (scoreStorageKey) =>
      isScoreKeyForMission(scoreStorageKey, missionId) &&
      hasScoreProperty(scores[scoreStorageKey], "score")
  );

const findPendingManagerValidationKey = (scores, missionId) =>
  Object.keys(scores).find(
    (scoreStorageKey) =>
      isScoreKeyForMission(scoreStorageKey, missionId) &&
      hasScoreProperty(scores[scoreStorageKey], "managerValidationScore") &&
      !hasScoreProperty(scores[scoreStorageKey], "score")
  );

const RenderICModule = ({
  fields,
  integerField,
  inlineRadioFields,
  moduleName,
  getActiveField,
  onChange,
  readOnly,
  values,
  errors,
  structure,
  header,
  moduleIcInfo,
  missionId,
  setScores,
}) => {
  const uniqueModuleId = `${missionId}_${moduleName}`;
  const score = calculateScore(values, inlineRadioFields, uniqueModuleId);
  const normVal = structure.normVal || 20;
  const maxVal = structure.maxVal || normVal;
  const normalizedScore =
    maxVal > 0 ? ((score * normVal) / maxVal).toFixed(2) : score;
  const maximizedScore = getFinalScore(normalizedScore, normVal);
  const total = normVal;
  const title = structure.label;
  const nbMonths = parseInt(
    fields
      .filter(
        (elm) =>
          elm.label === FIELD_LABEL.NB_MONTHS ||
          elm.label === FIELD_LABEL.NB_MONTHS_EN
      )
      .map((elm) => (elm.value ? elm.value : elm.default))[0]
  );

  useEffect(() => {
    setScores((prev) => {
      const updatedScores = { ...prev };
      if (updatedScores[uniqueModuleId]) {
        updatedScores[uniqueModuleId] = {
          ...updatedScores[uniqueModuleId],
          icModuleScore: { score: maximizedScore, nbMonths },
        };
      } else {
        updatedScores[uniqueModuleId] = {
          icModuleScore: { score: maximizedScore, nbMonths },
        };
      }
      return updatedScores;
    });
  }, [score, nbMonths, uniqueModuleId]);

  return (
    <div className="ic-module-container">
      <div
        style={{ display: "flex", flexDirection: "column", width: "80%" }}
        className="table-mission ic-separator"
      >
        <div className="mission-block">
          <div className="cell-padding">
            {integerField &&
              displayField({
                field: integerField,
                onChange,
                values,
                errors,
                readOnly,
              })}
          </div>
          {renderNameAndMissionDatesInputs({
            fields,
            onChange,
            values,
            errors,
            readOnly,
          })}
        </div>
        <Table
          id={"Ic-block-table"}
          header={header}
          content={
            formatContentListMission({
              fields: inlineRadioFields,
              moduleName,
              getActiveField,
              onChange,
              readOnly,
              values,
            }) || []
          }
          activeFields={moduleIcInfo?.activeFields}
        />
      </div>
      <MissionScore score={maximizedScore} total={total} title={title} />
    </div>
  );
};
RenderICModule.propTypes = {
  fields: PropTypes.array.isRequired,
  integerField: PropTypes.object,
  inlineRadioFields: PropTypes.array.isRequired,
  moduleName: PropTypes.string.isRequired,
  getActiveField: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  values: PropTypes.array.isRequired,
  errors: PropTypes.object,
  structure: PropTypes.object.isRequired,
  header: PropTypes.array,
  moduleIcInfo: PropTypes.object,
  missionId: PropTypes.string.isRequired,
  setScores: PropTypes.func.isRequired,
};

export const getSelectedValue = (value) => {
  if (_.isNil(value)) {
    return "";
  }

  if (typeof value !== "string") {
    return String(value);
  }

  try {
    const parsedValue = JSON.parse(value);

    if (Array.isArray(parsedValue)) {
      const normalizedValues = normalizeCheckboxValues(parsedValue);

      if (normalizedValues.length === 1) {
        return normalizedValues[0];
      }
    }
  } catch {
    return value;
  }

  return value;
};

const calculateScore = (values, inlineRadioFields, missionId) => {
  let totalScore = 0;
  inlineRadioFields.forEach((field) => {
    if (field.type !== FIELDS_TYPES.LABEL) {
      const fieldValues = JSON.parse(field.options).values;
      let baseMissionId = missionId;
      if (missionId.includes("_")) {
        baseMissionId = missionId.split("_")[0];
      }

      const selectedValuesList =
        baseMissionId === "none"
          ? values.filter((v) => v.field_id === field.field_id)
          : values.filter(
              (v) =>
                v.mission_id === baseMissionId && v.field_id === field.field_id
            );

      if (selectedValuesList?.length > 0) {
        const selectedValue = getSelectedValue(selectedValuesList[0].value);
        const rvalValues = fieldValues.find((r) => r.val === selectedValue);
        if (rvalValues) {
          totalScore += parseFloat(rvalValues.rval.replace(",", "."));
        }
      }
    }
  });
  return totalScore;
};

const MissionScore = ({ score, total, title }) => {
  const numericScore = Number(score) || 0;
  return (
    <div className="mission-score">
      <div className="mission-score-content">
        <div className="score-title">{title}</div>
        <div className="score-value">
          {numericScore.toFixed(2)}/{total}
        </div>
      </div>
    </div>
  );
};

MissionScore.propTypes = {
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.number,
  title: PropTypes.string,
};

const SubTotalScore = ({
  subTotal,
  score,
  subTitle,
  normVal = 20,
  maxVal = 20,
}) => {
  const numericSubTotal = Number(subTotal) || 0;
  const numericScore = Number(score) || 0;
  const calculatedScore =
    maxVal > 0 ? Math.round((numericScore * normVal) / maxVal) : 0;
  return (
    <div className="subTotal-field">
      <div className="subTotal-content">
        <div className="score-title">{subTitle}</div>
        <div className="score-indicator">
          {numericSubTotal.toFixed(2)} + {calculatedScore}
        </div>
        <div className="score-value">
          {(numericSubTotal + calculatedScore).toFixed(2)}/{normVal}
        </div>
      </div>
    </div>
  );
};

SubTotalScore.propTypes = {
  subTotal: PropTypes.number,
  score: PropTypes.number,
  subTitle: PropTypes.string,
  normVal: PropTypes.number,
  maxVal: PropTypes.number,
};

const RenderMissionBlock = ({
  fields,
  onChange,
  activeFields,
  getActiveField,
  moduleName,
  moduleId,
  values,
  errors,
  readOnly,
  header,
  inlineRadioFields,
  missionId,
  updateMissionScores,
  structure,
  setScores,
}) => {
  const parsedStructure =
    typeof structure === "string" ? JSON.parse(structure) : structure;
  const title = parsedStructure.label;
  const normVal = parsedStructure.normVal || MISSION_BALANCESHEET_TOTAL;
  const maxVal = parsedStructure.maxVal || normVal;
  const uniqueModuleId = `${missionId}_${moduleId}`;
  const score = calculateScore(values, inlineRadioFields, uniqueModuleId);
  const normalizedScore =
    maxVal > 0 ? (score * normVal).toFixed(2) / maxVal : score;

  const nbMonths = parseInt(
    fields
      .filter(
        (elm) =>
          elm.label === FIELD_LABEL.NB_MONTHS ||
          elm.label === FIELD_LABEL.NB_MONTHS_EN
      )
      .map((elm) => (elm.value ? elm.value : elm.default))[0]
  );

  useEffect(() => {
    setScores((prev) => {
      const updatedScores = { ...prev };
      const pendingManagerValidationKey = findPendingManagerValidationKey(
        updatedScores,
        missionId
      );
      const pendingManagerValidationScore = pendingManagerValidationKey
        ? updatedScores[pendingManagerValidationKey]?.managerValidationScore
        : undefined;

      updatedScores[uniqueModuleId] = {
        ...updatedScores[uniqueModuleId],
        score,
        nbMonths,
        ...(pendingManagerValidationScore !== undefined
          ? { managerValidationScore: pendingManagerValidationScore }
          : {}),
      };

      if (
        pendingManagerValidationKey &&
        pendingManagerValidationKey !== uniqueModuleId
      ) {
        delete updatedScores[pendingManagerValidationKey];
      }
      return updatedScores;
    });
  }, [missionId, nbMonths, uniqueModuleId, score]);

  return (
    <>
      <div
        style={{ display: "flex", flexDirection: "column", width: "80%" }}
        className="table-mission"
      >
        <div className="mission-block">
          {renderMissionBlockFields(
            fields,
            onChange,
            activeFields,
            getActiveField,
            moduleName,
            values,
            errors,
            readOnly
          )}
        </div>
        <Table
          id={"mission-block-table"}
          header={header}
          content={
            formatContentListMission({
              fields: inlineRadioFields,
              moduleName,
              getActiveField,
              activeFields,
              onChange,
              readOnly,
              values,
            }) || []
          }
          activeFields={activeFields}
        />
      </div>
      <MissionScore score={normalizedScore} total={normVal} title={title} />
    </>
  );
};
RenderMissionBlock.propTypes = {
  fields: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  activeFields: PropTypes.array.isRequired,
  getActiveField: PropTypes.func.isRequired,
  moduleName: PropTypes.string.isRequired,
  moduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  values: PropTypes.array.isRequired,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  header: PropTypes.array,
  inlineRadioFields: PropTypes.array.isRequired,
  missionId: PropTypes.string.isRequired,
  setScores: PropTypes.func.isRequired,
  updateMissionScores: PropTypes.func,
  structure: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const CalculateAverage = ({ scores, updateTotalScore, maxTotal, title }) => {
  const totalNbMonths = _.sumBy(
    Object.values(scores),
    (score) => (score.nbMonths || 0) + (score.icModuleScore?.nbMonths || 0)
  );

  const totalMissionBlockScore = _.sumBy(
    Object.values(scores),
    (score) =>
      ((score.score || 0) + (score.managerValidationScore || 0)) *
      (score.nbMonths || 0)
  );
  const totalIcModuleScore = _.sumBy(
    Object.values(scores),
    (score) =>
      (score.icModuleScore?.score || 0) * (score.icModuleScore?.nbMonths || 0)
  );

  const averageScore =
    (totalMissionBlockScore + totalIcModuleScore) / (totalNbMonths || 1);

  useEffect(() => {
    updateTotalScore(averageScore);
  }, [averageScore, updateTotalScore]);

  return (
    <div className="note-card">
      <div className="note-card-content">
        <div className="note-title">{title}</div>
        <div className="note-value">
          {averageScore.toFixed(2)}/{maxTotal}
        </div>
      </div>
    </div>
  );
};

CalculateAverage.propTypes = {
  scores: PropTypes.object,
  updateTotalScore: PropTypes.func,
  maxTotal: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
};
const CalculateTotal = ({
  totalScore,
  submodulesScores,
  maxTotal,
  title,
  scores,
  subTitle,
}) => {
  const finalTotalScore = totalScore + submodulesScores;

  const submodules = scores?.submodulesScores ? scores.submodulesScores : {};

  const combinedScores = Object.keys(submodules)
    .map((key) => `${submodules[key].totalScore}`)
    .join(" + ");

  return (
    <div className="parent-section-sum">
      <div className="section-sum">
        <div>{subTitle}</div>
        <div className="submodules-label">
          {totalScore.toFixed(2)} + {combinedScores}
        </div>
        <div className="title-label">{title}</div>
        <div className="total-score">
          {finalTotalScore.toFixed(2)}/{maxTotal}
        </div>
      </div>
    </div>
  );
};

CalculateTotal.propTypes = {
  totalScore: PropTypes.number,
  submodulesScores: PropTypes.number,
  maxTotal: PropTypes.number,
  title: PropTypes.string,
  scores: PropTypes.shape({ submodulesScores: PropTypes.object }),
  subTitle: PropTypes.string,
};

const RenderAverage = ({
  scores,
  type,
  totalScore,
  updateTotalScore,
  maxTotal,
  title,
  subTitle,
}) => {
  const missionScores = _.omit(scores, "submodulesScores");
  const submodulesScores = _.sumBy(
    Object.values(scores.submodulesScores || {}),
    (submodule) => submodule.totalScore || 0
  );
  if (type === BALANCESHEET_AVERAGES_TYPE.PROJECTS) {
    return (
      <CalculateAverage
        scores={missionScores}
        updateTotalScore={updateTotalScore}
        maxTotal={maxTotal}
        title={title}
      />
    );
  }

  if (type === BALANCESHEET_AVERAGES_TYPE.TOTAL) {
    return (
      <CalculateTotal
        totalScore={totalScore}
        submodulesScores={submodulesScores}
        maxTotal={maxTotal}
        title={title}
        scores={scores}
        subTitle={subTitle}
      />
    );
  }

  return null;
};

RenderAverage.propTypes = {
  scores: PropTypes.object,
  type: PropTypes.string,
  totalScore: PropTypes.number,
  updateTotalScore: PropTypes.func,
  maxTotal: PropTypes.number,
  title: PropTypes.string,
  subTitle: PropTypes.string,
};

const RenderMissionModuleMgr = ({
  fields,
  onChange,
  values,
  errors,
  readOnly,
  getSelectedValue,
  moduleName,
  inlineRadioFields,
  scores,
  setScores,
  missionId,
  structure,
  moduleId,
}) => {
  const sortedFields = _.sortBy(fields, "position").filter(
    (field) =>
      field.type === FIELDS_TYPES.INLINE_RADIO ||
      field.type === FIELDS_TYPES.TEXT_AREA
  );
  const uniqueModuleId = `${missionId}_${moduleId}`;
  const score = calculateScore(values, inlineRadioFields, uniqueModuleId);
  useEffect(() => {
    setScores((prev) => {
      const updatedScores = { ...prev };
      const missionScoreStorageKey = findMissionScoreStorageKey(
        updatedScores,
        missionId
      );

      if (missionScoreStorageKey) {
        updatedScores[missionScoreStorageKey] = {
          ...updatedScores[missionScoreStorageKey],
          managerValidationScore: score,
        };
        if (uniqueModuleId !== missionScoreStorageKey) {
          delete updatedScores[uniqueModuleId];
        }
      } else if (updatedScores[uniqueModuleId]) {
        updatedScores[uniqueModuleId].managerValidationScore = score;
      } else {
        updatedScores[uniqueModuleId] = {
          managerValidationScore: score,
          nbMonths: 0,
        };
      }
      return updatedScores;
    });
  }, [missionId, score, setScores, uniqueModuleId]);
  const missionScore = Object.entries(scores)
    .filter(([key]) => key.startsWith(`${missionId}_`))
    .reduce((acc, [, value]) => acc + (value.score || 0), 0);

  const parsedStructure =
    typeof structure === "string"
      ? JSON.parse(structure || "{}")
      : structure || {};
  const normVal = parsedStructure.normVal || MISSION_BALANCESHEET_TOTAL;
  const maxVal = parsedStructure.maxVal || MISSION_BALANCESHEET_TOTAL;
  return (
    <>
      <div
        className="manager-validation-block"
        style={{ display: "inline-flex", flexDirection: "row", width: "100%" }}
      >
        <div className="manager-validation-container">
          {sortedFields.map((field, index) => {
            const fieldClasses = `${
              field.type === FIELDS_TYPES.TEXT_AREA ? "input-cell" : ""
            } manager-validation-${field.type}`;
            const extraClass =
              field.type === FIELDS_TYPES.INLINE_RADIO
                ? "centered-radio-group "
                : "";
            return (
              <div
                className={`${fieldClasses} ${extraClass} centered-radio-group`}
                key={field.field_id}
              >
                {displayField({
                  field,
                  onChange,
                  values,
                  moduleName,
                  errors,
                  readOnly,
                  getSelectedValue,
                  selectionColor: colors.lightYellow,
                  lineId: parseFieldIdAndLine(field?.id).lineId,
                  inputStyle: { fontWeight: "bold" },
                  missionId,
                })}
              </div>
            );
          })}
        </div>
        <SubTotalScore
          subTotal={missionScore}
          score={score}
          subTitle={parsedStructure.subtitle}
          normVal={normVal}
          maxVal={maxVal}
        />
      </div>
    </>
  );
};

RenderMissionModuleMgr.propTypes = {
  fields: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  moduleName: PropTypes.string.isRequired,
  moduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inlineRadioFields: PropTypes.array.isRequired,
  scores: PropTypes.object.isRequired,
  setScores: PropTypes.func.isRequired,
  missionId: PropTypes.string.isRequired,
  structure: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  getSelectedValue: PropTypes.func,
};

const renderMissionModuleCnt = ({
  fields,
  onChange,
  values,
  readOnly,
  errors,
  isValidated,
  isExport,
  fileFieldConfig,
}) => {
  const visibleFields = fields.filter(
    (field) => field.type !== FIELDS_TYPES.HIDDEN
  );
  const contributionFields = _.chunk(visibleFields, 4);
  return (
    <div className="contribution-section">
      {contributionFields.map((group, groupIndex) => (
        <div key={groupIndex}>
          <div className="grid-container">
            {group.map((field, index) => {
              const { lineId } = parseFieldIdAndLine(field?.id);
              const className =
                field.type === "LABEL"
                  ? "label-contribution"
                  : `contribution-section-fields ${
                      index === 1 ? "first-field" : "last-fields"
                    }`;
              const hasError = Boolean(errors?.[`${field.field_id}_${lineId}`]);
              return (
                <div
                  key={field.id}
                  className={`${className} ${
                    isValidated && hasError ? "errorCellColor" : ""
                  }`}
                  style={isExport ? { height: "auto" } : undefined}
                >
                  {displayField({
                    field,
                    onChange,
                    values,
                    readOnly,
                    lineId,
                    inputStyle: { fontWeight: "bold" },
                    errors,
                    fileFieldConfig: {
                      ...fileFieldConfig,
                      moduleFields: fields,
                    },
                    isExport,
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export const renderLegend = (legend) => {
  const legendIcons = {
    "ellipse": EllipseLegendIcon,
    "blue-dot": BlueDotLegendIcon,
    "warning": WarningLegendIcon,
  };

  return (
    <>
      {Object.entries(legend).map(([key, item]) => {
        const LegendIcon = legendIcons[item.icon];

        return (
          <div
            key={key}
            style={{
              width: "40%",
              marginRight: "20px",
              marginLeft: "50px",
              alignItems: "center",
              display: "flex",
              flexDirection: "row",
              columnGap: "15px",
            }}
          >
            {LegendIcon ? (
              <LegendIcon
                aria-hidden="true"
                focusable="false"
                className="legend-second-module-icon"
              />
            ) : (
              <Icon icon={item.icon} color={item.color} />
            )}
            <Label label={item.lb} className={"label-position"} />
          </div>
        );
      })}
    </>
  );
};

const renderFields = (
  fields,
  onChange,
  values,
  errors,
  readOnly,
  width,
  isExport,
  fileFieldConfig
) => {
  if (!Array.isArray(fields)) {
    return null;
  }

  const hasCheckboxSelection = (value) =>
    normalizeCheckboxValues(value).length > 0;

  return fields.map((field) => {
    const isActive = values.some(
      (item) =>
        item.field_id === field.field_id &&
        (field?.type === FIELDS_TYPES.VERTICAL_CHECK
          ? hasCheckboxSelection(item.value)
          : item.value !== null && item.value !== "[]")
    );
    const activeValue = getSelectedValue(
      values.find((item) => item.field_id === field.field_id)?.value
    );
    const fieldOptions = fields.find(
      (item) => item.field_id === field.field_id
    )?.options;

    let rvalFieldValue = 0;
    let showWarningIcon = false;
    let hasOriginalOption = false;
    if (fieldOptions) {
      try {
        const parsedOptions = JSON.parse(fieldOptions);
        showWarningIcon = parsedOptions.warning || false;
        hasOriginalOption =
          parsedOptions?.values?.some((opt) => opt.original === true) || false;

        if (activeValue) {
          const correspondingOption = parsedOptions.values.find(
            (option) => option.val === activeValue
          );
          rvalFieldValue = correspondingOption?.rval || 0;
        }
      } catch (error) {
        console.error("failed to parse field options", error);
      }
    }
    return (
      <div
        key={field.field_id}
        className="second-module-fields"
        style={{ width }}
      >
        {field?.type === FIELDS_TYPES.LABEL ? (
          <div className="module-nt-labels">
            {displayField({
              field,
              onChange,
            })}
          </div>
        ) : field?.type === FIELDS_TYPES.VERTICAL_CHECK ? (
          <>
            <div className="active-value active-value--check">
              <div
                className={`vertical-check ${
                  isActive ? "vertical-check--active" : ""
                }`}
              >
                {displayField({
                  field,
                  onChange,
                  values,
                  errors,
                  readOnly,
                  fileFieldConfig: {
                    ...fileFieldConfig,
                    moduleFields: fields,
                  },
                  isExport,
                })}
              </div>
              <div className="vertical-line vertical-line--check"></div>
              <div className="field-value field-value--check">
                {rvalFieldValue}
                {showWarningIcon && <Icon icon="warning" color="gamboge" />}
                {hasOriginalOption && (
                  <Icon
                    icon="ellipse"
                    color="red"
                    height={25}
                    width={15}
                    marginLeft={5}
                  />
                )}
              </div>
            </div>
          </>
        ) : field?.type === FIELDS_TYPES.RANGE ? (
          <>
            <div className="active-value active-value--range">
              <div className="range-fields">
                {displayField({
                  field,
                  onChange,
                  values,
                  errors,
                  readOnly,
                  fileFieldConfig: {
                    ...fileFieldConfig,
                    moduleFields: fields,
                  },
                  isExport,
                })}
              </div>
              <div className="vertical-line vertical-line--range"></div>
              <div className="field-value field-value--range">
                {rvalFieldValue}
                {showWarningIcon && <Icon icon="warning" color="gamboge" />}
              </div>
            </div>
          </>
        ) : field?.type === FIELDS_TYPES.VERTICAL_RADIO ? (
          <>
            <div className="active-value active-value--vertical-radio">
              <div className="vertical-radio-fields">
                {displayField({
                  field,
                  onChange,
                  values,
                  errors,
                  readOnly,
                  selectionColor: colors.mintTulip,
                  fileFieldConfig: {
                    ...fileFieldConfig,
                    moduleFields: fields,
                  },
                  isExport,
                })}
              </div>
              <div className="vertical-line-vertical-radio"></div>
              <div className="field-value field-value--vertical-radio">
                {rvalFieldValue}
                {showWarningIcon && <Icon icon="warning" color="gamboge" />}
              </div>
            </div>
          </>
        ) : (
          <div className="input-cell">
            {displayField({
              field,
              onChange,
              values,
              errors,
              readOnly,
              fileFieldConfig: {
                ...fileFieldConfig,
                moduleFields: fields,
              },
              isExport,
            })}
          </div>
        )}
      </div>
    );
  });
};

const renderFieldsInColumns = (
  fields,
  structure,
  onChange,
  values,
  errors,
  readOnly,
  isExport,
  fileFieldConfig
) => {
  const columns = Object.keys(structure).reduce((acc, colKey) => {
    acc[colKey] = [];
    return acc;
  }, {});

  let position = 0;

  Object.keys(structure).forEach((colKey) => {
    const colRange = structure[colKey].r;

    _.times(colRange, () => {
      if (position < fields.length) {
        columns[colKey].push(fields[position]);
        position += 1;
      }
    });
  });

  return (
    <div style={{ width: "100%", display: "flex" }}>
      {Object.keys(columns).map((colKey) => (
        <div
          key={colKey}
          style={{ width: structure[colKey].w }}
          className="second-module"
        >
          {renderFields(
            columns[colKey],
            onChange,
            values,
            errors,
            readOnly,
            "100%",
            isExport,
            fileFieldConfig
          )}
        </div>
      ))}
    </div>
  );
};

const getFinalScore = (score, max) => (score > max ? max : score);

const RenderSubmodulesNT = ({
  submodules,
  structureNT,
  onChange,
  values,
  errors,
  readOnly,
  setScores,
  maxModuleScore,
  missionId,
  isExport,
  fileFieldConfig,
}) => {
  const structure = structureNT.cols;
  const title = structureNT.title;
  let position = 0;
  const total = maxModuleScore;

  const [localScores, setLocalScores] = useState({});

  useEffect(() => {
    const newLocalScores = {};
    const submodulesAverageScore = {};

    submodules.forEach((submodule) => {
      let submoduleScore = 0;
      const submoduleFieldsScores = {};

      submodule.fields.forEach((field) => {
        if (field.type !== FIELDS_TYPES.LABEL) {
          const fieldId = `${submodule.module_id}_${field.field_id}`;
          const score = calculateScore(values, [field], missionId);
          newLocalScores[fieldId] = score;
          submoduleFieldsScores[fieldId] = score;
          submoduleScore += score;
        }
      });

      const finalScore = getFinalScore(submoduleScore, maxModuleScore);
      submodulesAverageScore[submodule.module_id] = {
        totalScore: finalScore,
        fieldsScores: submoduleFieldsScores,
      };
    });

    const groupedScores = {};
    submodules.forEach((submodule) => {
      const parentId = submodule.parent_module_id;
      if (!groupedScores[parentId]) {
        groupedScores[parentId] = {
          totalScore: 0,
          fieldsScores: {},
        };
      }
      groupedScores[parentId].totalScore +=
        submodulesAverageScore[submodule.module_id]?.totalScore || 0;
      groupedScores[parentId].fieldsScores = {
        ...groupedScores[parentId].fieldsScores,
        ...submodulesAverageScore[submodule.module_id]?.fieldsScores,
      };
    });

    Object.keys(groupedScores).forEach((parentId) => {
      groupedScores[parentId].totalScore = getFinalScore(
        groupedScores[parentId].totalScore,
        maxModuleScore
      );
    });

    setLocalScores(groupedScores);

    setScores((prev) => {
      const updatedScores = { ...prev };
      if (!updatedScores.submodulesScores) {
        updatedScores.submodulesScores = {};
      }
      if (missionId === "none") {
        updatedScores.submodulesScores = {
          ...updatedScores.submodulesScores,
          ...groupedScores,
        };
      }
      return updatedScores;
    });
  }, [submodules, values, setScores, missionId]);

  const submoduleScores = Object.values(localScores).map(
    (score) => score.totalScore
  );

  const totalLocalScore = _.sum(submoduleScores);
  const finalScore = getFinalScore(totalLocalScore, maxModuleScore);

  useEffect(() => {
    setScores((prev) => {
      const updatedScores = { ...prev };
      updatedScores.finalScore = finalScore;
      return updatedScores;
    });
  }, [finalScore, setScores]);

  return (
    <>
      {Object.keys(structure).map((colKey) => (
        <div
          key={colKey}
          style={{
            width: structure[colKey].w,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {_.times(structure[colKey].r, () => {
            if (position >= submodules.length) return null;

            const submodule = submodules[position];
            position += 1;

            const parsedSubmoduleStructure = JSON.parse(submodule.structure);

            return (
              <>
                {renderFieldsInColumns(
                  submodule.fields,
                  parsedSubmoduleStructure.cols,
                  onChange,
                  values,
                  errors,
                  readOnly,
                  isExport,
                  {
                    ...fileFieldConfig,
                    moduleId: submodule.module_id,
                  }
                )}
              </>
            );
          })}
        </div>
      ))}
      <MissionScore score={finalScore} total={total} title={title} />
    </>
  );
};

RenderSubmodulesNT.propTypes = {
  submodules: PropTypes.arrayOf(
    PropTypes.shape({
      fields: PropTypes.array,
      module_id: PropTypes.string,
      parent_module_id: PropTypes.string,
      structure: PropTypes.string,
    })
  ),
  structureNT: PropTypes.shape({
    cols: PropTypes.object,
    title: PropTypes.string,
  }),
  onChange: PropTypes.func,
  values: PropTypes.array,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  setScores: PropTypes.func,
  maxModuleScore: PropTypes.number,
  missionId: PropTypes.string,
  isExport: PropTypes.bool,
  fileFieldConfig: PropTypes.object,
};

const renderMissionManager = ({
  cols,
  fields,
  onChange,
  values,
  errors,
  readOnly,
  isExport,
  fileFieldConfig,
}) => {
  // remove duplicates
  const uniqFields = _.uniqBy(fields, "id");

  const grouped = _.groupBy(uniqFields, (f) => String(f.id).split("_")[1]);
  const groupIds = Object.keys(grouped).sort((a, b) => a - b);
  return (
    <div style={{ width: "100%" }}>
      {groupIds.map((gid) => {
        let position = 0; // to reset the bloc

        return (
          <div
            key={gid}
            style={{ display: "flex", flexDirection: "row", width: "100%" }}
          >
            {Object.keys(cols).map((elm) => (
              <div
                key={`${gid}-${elm}`}
                style={{
                  width: cols[elm]?.w,
                  display: "flex",
                  flexDirection: "column",
                  paddingBottom: "20px",
                }}
              >
                {_.times(cols[elm]?.r, () => {
                  position = position + 1;

                  const field = _.chain(grouped[gid])
                    .filter((f) => f?.position === position)
                    .head()
                    .value();

                  const extraClass =
                    field?.type === FIELDS_TYPES.TEXT_AREA
                      ? "eval-manager-field"
                      : field?.label === ""
                        ? "empty-row"
                        : "";

                  let optionClass = "";
                  let multilineReadOnly = false;

                  const options = field?.options
                    ? JSON.parse(field?.options)
                    : null;
                  optionClass = options?.className ?? "";
                  multilineReadOnly =
                    optionClass === BALANCESHEET_CSS_CLASSES.MISSION_ROW;

                  if (!_.isEmpty(field)) {
                    return (
                      <div
                        key={`${gid}-${field.id}`}
                        className={`${extraClass} ${optionClass}`}
                      >
                        {displayField({
                          field,
                          onChange,
                          values,
                          errors,
                          readOnly,
                          lineId: parseFieldIdAndLine(field?.id).lineId,
                          dynamicHeight:
                            field?.type === FIELDS_TYPES.TEXT_AREA,
                          inputStyle: { fontWeight: "bold" },
                          fileFieldConfig: {
                            ...fileFieldConfig,
                            moduleFields: fields,
                          },
                          multilineReadOnly,
                          isExport,
                        })}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const renderMainModule = ({
  mainModule,
  onChange,
  values,
  errors,
  readOnly,
  legend,
  scores,
  setScores,
  isExport,
  fileUploadConfig,
}) => {
  const structure = JSON.parse(mainModule.structure).cols;
  const submodulesNT = mainModule.modules.filter(
    (module) => module.type === MODULES_TYPES.MR_SECOND_MODULE_NT
  );

  return (
    <div style={{ width: structure.col1.w }}>
      {legend && (
        <>
          <div className="legend-second-module-main">
            {renderLegend(legend)}
          </div>
          <div className="block-separator"></div>
        </>
      )}
      {Object.keys(structure).map((colKey) => (
        <div
          key={colKey}
          style={{
            width: structure[colKey].w,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {_.times(structure[colKey].r, (index) => {
            if (index >= submodulesNT.length) return null;

            const submoduleNT = submodulesNT[index];
            const parsedNTtructure = JSON.parse(submoduleNT.structure);
            const maxModuleScore = parsedNTtructure.moduleMax;
            const missionId = submoduleNT.mission_id;
            return (
              <div
                key={submoduleNT.module_id}
                className="second-module-nt-row"
                style={{ width: "100%", display: "flex", gap: "20px" }}
              >
                <RenderSubmodulesNT
                  submodules={submoduleNT.modules}
                  structureNT={parsedNTtructure}
                  onChange={onChange}
                  values={values}
                  errors={errors}
                  readOnly={readOnly}
                  setScores={setScores}
                  scores={scores}
                  maxModuleScore={maxModuleScore}
                  missionId={missionId}
                  isExport={isExport}
                  fileFieldConfig={fileUploadConfig}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const transformDynamicStyle = (styleString) => {
  if (!styleString) return {};

  return styleString
    .split(";")
    .filter((style) => style.trim() !== "")
    .reduce((acc, style) => {
      let [key, value] = style.split(":").map((str) => str.trim());

      if (key && value) {
        key = key.replace(/^["']|["']$/g, "");
        value = value.replace(/^["']|["']$/g, "");

        const camelCasedKey = key.replace(/-([a-z])/g, (g) =>
          g[1].toUpperCase()
        );
        acc[camelCasedKey] = value;
      }
      return acc;
    }, {});
};

// A field styled with a negative margin-left (e.g. "margin-left: -127px") is
// drawn over the right edge of the cell sitting on the same row in the previous
// column. Returns, keyed by that neighbour's position, how many px it must give
// up so its content no longer runs under the overlapping cell.
const getOverlapByPosition = (cols, fields) => {
  const rowsPerColumn = Object.keys(cols).map((key) => cols[key]?.r || 0);
  const columnStarts = rowsPerColumn.reduce(
    (acc, rows, index) => [...acc, acc[index] + rows],
    [0]
  );

  return fields.reduce((acc, field) => {
    const marginLeft = transformDynamicStyle(
      JSON.parse(field?.options || "{}")?.style
    )?.marginLeft;
    const overlap = /^-(\d+(?:\.\d+)?)px$/.exec(marginLeft || "");
    if (!overlap) {
      return acc;
    }

    const columnIndex = columnStarts.findIndex(
      (start, index) =>
        index < rowsPerColumn.length &&
        field.position > start &&
        field.position <= start + rowsPerColumn[index]
    );
    if (columnIndex <= 0) {
      return acc;
    }

    const rowIndex = field.position - columnStarts[columnIndex] - 1;
    if (rowIndex >= rowsPerColumn[columnIndex - 1]) {
      return acc;
    }

    const neighbourPosition = columnStarts[columnIndex - 1] + rowIndex + 1;
    return { ...acc, [neighbourPosition]: Number(overlap[1]) };
  }, {});
};

// A field style may carry pixel widths and margins, authored against the
// module's 1320px canvas. Expressed as a percentage of the column they live in,
// they keep the same proportions once the module shrinks with the viewport.
const endsWith = (value, unit) => String(value ?? "").endsWith(unit);

// "20%" -> 264px, the column's width on the 1320px canvas.
const getColumnDesignWidth = (columnWidth) =>
  endsWith(columnWidth, "%")
    ? (parseFloat(columnWidth) / 100) * BALANCESHEET_DESIGN_WIDTH
    : null;

// "655px" in a 264px column -> "248.1061%". Anything else is left as is.
const toColumnPercent = (value, columnDesignWidth) =>
  endsWith(value, "px") && columnDesignWidth
    ? `${((parseFloat(value) / columnDesignWidth) * 100).toFixed(4)}%`
    : value;

const getActiveRowGroups = (fields, values) => {
  return fields.reduce((acc, field) => {
    if (field.type === FIELDS_TYPES.INLINE_CHECK) {
      const isActiveCheck = values.some(
        (item) => item.field_id === field.field_id && item.value !== null
      );

      if (isActiveCheck) {
        const rowGroup = JSON.parse(field.options)?.row_group;
        if (rowGroup && !acc.includes(rowGroup)) {
          acc.push(rowGroup);
        }
      }
    }

    return acc;
  }, []);
};

const getFieldClassNames = (field, isActive) => {
  const classNameForVerticalRadio =
    field?.type === FIELDS_TYPES.VERTICAL_RADIO ? "vertical-radio-group" : "";
  const classNameForInlineCheck =
    field?.type === FIELDS_TYPES.INLINE_CHECK ? "inline-check" : "";
  const classNameLabel =
    field?.type === FIELDS_TYPES.LABEL
      ? `${
          JSON.parse(field?.options)?.class ? "custom-label-Field-style" : ""
        } ${JSON.parse(field?.options)?.style ? "label-text-style" : ""}`
      : "";

  const highlightClass = isActive ? "highlighted" : "";
  const classNameForEntity =
    field?.type === FIELDS_TYPES.ENTITY ? "entity-field" : "";
  const classNameForRowGroup = JSON.parse(field?.options || "{}")?.row_group
    ? "row-group-field"
    : "";

  return `${classNameForVerticalRadio} ${classNameForInlineCheck} ${classNameLabel} input-cell ${highlightClass} ${classNameForEntity} ${classNameForRowGroup}`;
};
const handleFieldChange = ({ field, value, id, onChange, fields }) => {
  const fieldOptions = field.options ? JSON.parse(field.options) : {};
  const isSalaryField = !!fieldOptions.class;

  const suffix = id && id.includes("_") ? id.substring(id.indexOf("_")) : "";

  const currentId = id || `${field.field_id}${suffix}`;
  onChange({
    field_id: field.field_id,
    id: currentId,
    value,
  });

  if (field.type !== FIELDS_TYPES.NUMBER || !isSalaryField) return;

  const relatedFieldId = parseInt(
    fieldOptions[BALANCESHEET_SALARY_FIELDS.RELATED_FIELD]
  );
  const sourceFields = fieldOptions[BALANCESHEET_SALARY_FIELDS.SOURCE_FIELDS]
    ?.split(",")
    .map(Number);

  if (!relatedFieldId || !sourceFields) return;

  let salaryFieldId, percentageFieldId;
  [salaryFieldId, percentageFieldId] = sourceFields;

  const salaryField = fields.find((item) => item.field_id === salaryFieldId);
  const percentageField = fields.find(
    (item) => item.field_id === percentageFieldId
  );

  const numericValue = parseNumberWithDecimalSeparator(value);

  const relatedId = `${relatedFieldId}${suffix}`;

  if (isNaN(numericValue)) {
    if (field.field_id === percentageFieldId) {
      onChange({ field_id: relatedFieldId, id: relatedId, value: "0" });
    } else if (field.field_id === salaryFieldId) {
      onChange({ field_id: relatedFieldId, id: relatedId, value: "0" });
    }
    return;
  }

  if (field.field_id === percentageFieldId && salaryField) {
    const salaryValue = parseNumberWithDecimalSeparator(
      salaryField.value || salaryField.default || 0
    );

    if (!isNaN(salaryValue) && salaryValue > 0) {
      const percentageValue = numericValue;
      const newSalary = salaryValue + salaryValue * (percentageValue / 100);

      onChange({
        field_id: relatedFieldId,
        id: relatedId,
        value: newSalary.toFixed(2),
      });
    } else {
      onChange({ field_id: relatedFieldId, id: relatedId, value: "0" });
    }
    return;
  }

  if (field.field_id === salaryFieldId && percentageField) {
    const newSalaryValue = numericValue;
    const originalSalaryValue = parseNumberWithDecimalSeparator(
      salaryField.value || salaryField.default || 0
    );

    if (originalSalaryValue > 0) {
      if (isNaN(newSalaryValue) || newSalaryValue === 0) {
        onChange({ field_id: relatedFieldId, id: relatedId, value: "0" });
      } else {
        const newPercentage =
          ((newSalaryValue - originalSalaryValue) / originalSalaryValue) * 100;
        onChange({
          field_id: relatedFieldId,
          id: relatedId,
          value: newPercentage.toFixed(2),
        });
      }
    } else {
      onChange({ field_id: relatedFieldId, id: relatedId, value: "0" });
    }
  }
};

const renderGenericField = ({
  field,
  onChange,
  values,
  errors,
  readOnly,
  isActive,
  isActiveRowGroups,
  fieldStyle,
  dynamicHeight,
  fields,
  isValidated,
  isExport,
  fileFieldConfig,
}) => {
  const parsedOptions = JSON.parse(field.options || "{}");
  const readOnlyOption = parsedOptions?.["read-only"];
  const isFieldReadOnly =
    readOnlyOption === true || readOnlyOption === "true";
  const className = getFieldClassNames(field, isActive);
  const rowGroup = parsedOptions?.row_group;
  const isEditable =
    field.type === FIELDS_TYPES.INLINE_CHECK ||
    !rowGroup ||
    isActiveRowGroups.includes(rowGroup);

  let filteredOptions = [];
  if (field?.type === FIELDS_TYPES.CHOICE && field?.options) {
    const allOptions = parsedOptions?.values || [];
    const currentChoiceValue = getFieldValueById(values, field.field_id);
    const hasCurrentChoiceValue =
      !_.isNil(currentChoiceValue) && currentChoiceValue !== "";
    const dashChoiceOption = allOptions.find((option) => option.val === "-");

    if (isActive) {
      filteredOptions = allOptions.filter((option) => option.val !== "-");
      if (!hasCurrentChoiceValue && dashChoiceOption) {
        filteredOptions = [dashChoiceOption, ...filteredOptions];
      }
    } else {
      filteredOptions = allOptions;
    }
  }
  const fieldKey = `${field.field_id}_1`;

  const hasError =
    Boolean(errors?.[fieldKey]) ||
    Boolean(
      Array.isArray(errors) && errors.some((err) => err.field_id === fieldKey)
    );

  const dashOption = filteredOptions.find((option) => option.val === "-")?.val;

  if (field?.type === FIELDS_TYPES.IMAGE) {
    return (
      <div key={field.field_id} style={{ width: fieldStyle?.w }}>
        {displayField({ field, onChange })}
      </div>
    );
  }

  if (field?.type === FIELDS_TYPES.INLINE_CHECK) {
    return (
      <div className={`${className}`} style={fieldStyle}>
        {displayField({
          field,
          onChange,
          values,
          errors,
          readOnly: isFieldReadOnly || readOnly || !isEditable,
          isExport,
        })}
      </div>
    );
  }
  return (
    <div
      className={`${className} ${
        isValidated && hasError ? "errorCellColor" : ""
      }`}
      style={fieldStyle}
    >
      {displayField({
        field,
        onChange: (updatedField) =>
          handleFieldChange({
            field,
            value: updatedField.value,
            id: updatedField.id,
            onChange,
            fields,
          }),
        values,
        errors,
        readOnly: isFieldReadOnly || readOnly || !isEditable,
        disableCalendar: false,
        inputStyle: { fontWeight: "bold" },
        dynamicHeight,
        optionsForChoice: filteredOptions,
        dashOption,
        fieldStyle,
        fileFieldConfig,
        isExport,
      })}
    </div>
  );
};

const RenderColumns = ({
  cols,
  fields,
  onChange,
  values,
  readOnly,
  errors,
  structure,
  isValidated,
  isExport,
  fileFieldConfig,
}) => {
  const dynamicHeight = structure.dynamicHeight;
  let position = 0;
  const activeRowGroups = getActiveRowGroups(fields, values);
  const previousActiveRowGroupsRef = useRef([]);
  const columnKeys = Object.keys(cols);
  useEffect(() => {
    previousActiveRowGroupsRef.current.forEach((rowGroup) => {
      if (!activeRowGroups.includes(rowGroup)) {
        fields.forEach((field) => {
          const fieldRowGroup = JSON.parse(field.options)?.row_group;
          if (fieldRowGroup === rowGroup) {
            const currentValue = values.find(
              (item) => item.field_id === field.field_id
            )?.value;
            if (currentValue) {
              onChange({
                field_id: field.field_id,
                value: "",
              });
            }
          }
        });
      }
    });

    previousActiveRowGroupsRef.current = activeRowGroups;
  }, [activeRowGroups]);

  const overlapByPosition = getOverlapByPosition(cols, fields);

  const getFieldStyleForRender = (field, columnWidth) => {
    const dynamicStyleString = field?.options
      ? JSON.parse(field?.options)?.style || ""
      : "";
    const fieldStyle = transformDynamicStyle(dynamicStyleString);
    // The PDF export renders at its own fixed width: leave its styles untouched.
    const columnDesignWidth = isExport
      ? null
      : getColumnDesignWidth(columnWidth);
    const scaledStyle = columnDesignWidth
      ? {
          ...fieldStyle,
          width: toColumnPercent(fieldStyle.width, columnDesignWidth),
          marginLeft: toColumnPercent(fieldStyle.marginLeft, columnDesignWidth),
          marginRight: toColumnPercent(
            fieldStyle.marginRight,
            columnDesignWidth
          ),
        }
      : fieldStyle;
    const overlap = overlapByPosition[field?.position];
    if (!overlap) {
      return scaledStyle;
    }
    return {
      ...scaledStyle,
      width: `calc(${scaledStyle.width || "100%"} - ${toColumnPercent(
        `${overlap}px`,
        columnDesignWidth
      )})`,
    };
  };

  return columnKeys.map((elm) => (
    <div
      style={{
        width: cols[elm]?.w,
        display: "flex",
        flexDirection: "column",
        paddingBottom: "30px",
      }}
    >
      {_.times(cols[elm]?.r, () => {
        position += 1;
        const field = _.chain(fields)
          .filter((elm) => elm.position === position)
          .head()
          .value();

        if (!_.isEmpty(field)) {
          const rowGroup = JSON.parse(field.options)?.row_group;
          const isActive = activeRowGroups.includes(rowGroup);
          return (
            <>
              {renderGenericField({
                field,
                onChange,
                values,
                errors,
                readOnly,
                isActive,
                isActiveRowGroups: activeRowGroups,
                fieldStyle: getFieldStyleForRender(field, cols[elm]?.w),
                dynamicHeight,
                fields,
                isValidated,
                fileFieldConfig: {
                  ...fileFieldConfig,
                  moduleFields: fields,
                },
                isExport,
              })}
            </>
          );
        }
        return <></>;
      })}
    </div>
  ));
};

RenderColumns.propTypes = {
  cols: PropTypes.object,
  fields: PropTypes.array,
  onChange: PropTypes.func,
  values: PropTypes.array,
  readOnly: PropTypes.bool,
  errors: PropTypes.object,
  structure: PropTypes.object,
  isValidated: PropTypes.bool,
  isExport: PropTypes.bool,
  fileFieldConfig: PropTypes.object,
};

//This function is called when mapping modules, and according to each type of module, it displays corresponding data
export const displayStructure = ({
  activeFields,
  values,
  dynamicTable,
  errors,
  isValidated,
  fields,
  getActiveField,
  getSelectedValue,
  moduleIcInfo,
  moduleName,
  onChange,
  onChangeTableValues,
  readOnly,
  structure,
  type,
  modules,
  scores,
  setScores,
  missionAverage,
  setMissionAverage,
  updateMissionScores,
  updateMissionAverage,
  missionId,
  updateTotalScore,
  totalScore,
  rows,
  setRows,
  moduleId,
  isExport,
  fileUploadConfig,
}) => {
  const formattedStructure = structure ? parseStructure(structure) : {};
  const cols = formattedStructure?.cols || {};
  const legend = formattedStructure?.legend || {};
  const integerField = getIntegerField(fields);
  const inlineRadioFields = getInlineRadioFields(fields);
  let header = [];

  switch (type) {
    case MODULES_TYPES.LIST: {
      header = formatHeaderList({ structure: formattedStructure });
      return renderTable({
        fields,
        values,
        onChange,
        readOnly,
        activeFields,
        getActiveField,
        getSelectedValue,
        moduleName,
        header,
        isExport,
      });
    }
    case MODULES_TYPES.TABLE: {
      const isEditable = formattedStructure?.delete;
      return (
        <RenderTbaleBalancesheet
          fields={fields}
          isEditable={isEditable}
          formattedStructure={formattedStructure}
          readOnly={readOnly}
          dynamicTable={dynamicTable}
          onChangeTableValues={onChangeTableValues}
          values={values}
          disableCalendar={false}
          rows={rows}
          setRows={setRows}
          moduleId={moduleId}
          isValidated={isValidated}
          errors={errors}
          isExport={isExport}
        />
      );
    }
    case MODULES_TYPES.MR_IC_MODULE: {
      header = formatHeaderList({ structure: JSON.parse(structure) });
      return (
        <RenderICModule
          missionId={missionId}
          fields={fields}
          inlineRadioFields={inlineRadioFields}
          onChange={onChange}
          activeFields={activeFields}
          getActiveField={getActiveField}
          moduleName={moduleName}
          values={values}
          errors={errors}
          isValidated={isValidated}
          readOnly={readOnly}
          header={header}
          structure={formattedStructure}
          scores={scores}
          setScores={setScores}
          missionAverage={missionAverage}
          setMissionAverage={setMissionAverage}
          integerField={integerField}
          moduleIcInfo={moduleIcInfo}
          isExport={isExport}
        />
      );
    }
    case MODULES_TYPES.MR_MISSION_MODULE: {
      header = formatHeaderList({ structure: JSON.parse(structure) });
      return (
        <RenderMissionBlock
          fields={fields}
          onChange={onChange}
          activeFields={activeFields}
          getActiveField={getActiveField}
          moduleName={moduleName}
          moduleId={moduleId}
          values={values}
          errors={errors}
          isValidated={isValidated}
          readOnly={readOnly}
          header={header}
          getSelectedValue={getSelectedValue}
          inlineRadioFields={inlineRadioFields}
          scores={scores}
          setScores={setScores}
          missionAverage={missionAverage}
          setMissionAverage={setMissionAverage}
          structure={formattedStructure}
          updateMissionScores={updateMissionScores}
          updateMissionAverage={updateMissionAverage}
          missionId={missionId}
        />
      );
    }
    case MODULES_TYPES.MR_MISSION_MODULE_CNT: {
      return renderMissionModuleCnt({
        cols,
        fields,
        onChange,
        values,
        errors,
        isValidated,
        readOnly,
        getSelectedValue,
        moduleName,
        fileFieldConfig: {
          ...fileUploadConfig,
          moduleId,
        },
        isExport,
      });
    }
    case MODULES_TYPES.MR_MISSION_MODULE_MGR: {
      return (
        <RenderMissionModuleMgr
          fields={fields}
          onChange={onChange}
          getSelectedValue={getSelectedValue}
          moduleName={moduleName}
          moduleId={moduleId}
          values={values}
          errors={errors}
          readOnly={readOnly}
          inlineRadioFields={inlineRadioFields}
          scores={scores}
          setScores={setScores}
          setMissionAverage={setMissionAverage}
          missionId={missionId}
          structure={formattedStructure}
          isValidated={isValidated}
          isExport={isExport}
        />
      );
    }
    case MODULES_TYPES.MR_NOTE: {
      return (
        <RenderAverage
          fields={fields}
          onChange={onChange}
          getSelectedValue={getSelectedValue}
          moduleName={moduleName}
          values={values}
          errors={errors}
          readOnly={readOnly}
          inlineRadioFields={inlineRadioFields}
          scores={scores}
          setScores={setScores}
          missionAverage={missionAverage}
          setMissionAverage={setMissionAverage}
          title={formattedStructure.title}
          type={formattedStructure.type}
          updateTotalScore={updateTotalScore}
          totalScore={totalScore}
          maxTotal={formattedStructure.normVal}
          subTitle={formattedStructure.subtitle}
          isExport={isExport}
        />
      );
    }
    case MODULES_TYPES.MR_SECOND_MODULE_MAIN:
      return renderMainModule({
        mainModule: { structure, modules },
        onChange,
        values,
        errors,
        readOnly,
        legend,
        scores,
        isValidated,
        setScores,
        isExport,
        fileUploadConfig: {
          ...fileUploadConfig,
          moduleId,
        },
      });
    case MODULES_TYPES.MR_MISSIONMANAGER:
      return renderMissionManager({
        cols,
        fields,
        onChange,
        values,
        errors,
        isValidated,
        readOnly,
        fileFieldConfig: {
          ...fileUploadConfig,
          moduleId,
        },
        isExport,
      });

    default:
      return (
        <RenderColumns
          cols={cols}
          fields={fields}
          onChange={onChange}
          values={values}
          readOnly={readOnly}
          structure={formattedStructure}
          errors={errors}
          isValidated={isValidated}
          fileFieldConfig={{
            ...fileUploadConfig,
            moduleId,
          }}
          isExport={isExport}
        />
      );
  }
};

//this function is used to get RVAL for INLINE_RADIO in module LIST
export const filterData = ({ data, id }) => {
  const field = _.find(data, (elm) => elm.id === id);
  if (field) {
    const rval = JSON.parse(field?.options || {})?.values?.find(
      (elm) => elm?.val === field?.value
    )?.rval;
    return _.chain(rval).replace(",", ".").toNumber().value();
  }
  return "";
};

export const normalizeCheckboxValues = (value) => {
  const normalizeCheckboxItem = (item) => {
    const normalizedItem = String(item ?? "").trim();
    return /^-?\d+,\d+$/.test(normalizedItem)
      ? normalizedItem.replace(",", ".")
      : normalizedItem;
  };

  const normalizeValuesList = (valuesList) =>
    _.chain(valuesList)
      .map(normalizeCheckboxItem)
      .filter((item) => item !== "")
      .uniq()
      .value();

  if (Array.isArray(value)) {
    return normalizeValuesList(value);
  }

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? normalizeValuesList(parsedValue) : [];
    } catch {
      return value ? normalizeValuesList([value]) : [];
    }
  }

  return [];
};

const EMPTY_CHECKBOX_VALUE = JSON.stringify([]);

const toCheckboxSequenceValue = (rawValue) => {
  if (_.isNil(rawValue) || rawValue === "") {
    return null;
  }

  try {
    const parsedValue = Array.isArray(rawValue)
      ? rawValue
      : JSON.parse(rawValue);
    if (Array.isArray(parsedValue)) {
      return JSON.stringify(normalizeCheckboxValues(parsedValue));
    }
  } catch {
    return EMPTY_CHECKBOX_VALUE;
  }

  return EMPTY_CHECKBOX_VALUE;
};

const getCheckboxValue = ({ value, defaultValue }) =>
  toCheckboxSequenceValue(value) ??
  toCheckboxSequenceValue(defaultValue) ??
  EMPTY_CHECKBOX_VALUE;

const getCorrespondValue = ({ field }) => {
  const { type, value, options, default: defaultValue } = field || {};
  const parsedOptions = JSON.parse(options || "{}");
  const firstOptionValue = parsedOptions?.values?.[0];

  switch (type) {
    case FIELDS_TYPES.INLINE_RADIO:
    case FIELDS_TYPES.VERTICAL_RADIO:
      return value || firstOptionValue?.rval || firstOptionValue?.val;
    case FIELDS_TYPES.INLINE_CHECK:
    case FIELDS_TYPES.VERTICAL_CHECK:
      return getCheckboxValue({ value, defaultValue });
    case FIELDS_TYPES.TEXT:
    case FIELDS_TYPES.TEXT_AREA:
    case FIELDS_TYPES.CHOICE:
    case FIELDS_TYPES.DATE:
      return value || defaultValue;
    default:
      return null;
  }
};

//sans field table
export const initForm = ({ fields, missionId }) =>
  fields.reduce(
    (prev, curr) => [
      ...prev,
      {
        id: curr?.id || `${curr?.field_id}_1`,
        field_id: curr.field_id,
        required: curr.required,
        default: curr.default,
        mission_id: missionId,
        value: initFieldValue(curr),
      },
    ],
    []
  );

const initFieldValue = (field) => {
  const isRadioOrCheck =
    field?.type === FIELDS_TYPES.INLINE_RADIO ||
    field?.type === FIELDS_TYPES.VERTICAL_RADIO ||
    field?.type === FIELDS_TYPES.RANGE;

  if (isRadioOrCheck && !field?.value) {
    return JSON.parse(field?.options || {})?.values[0]?.val;
  }

  if (
    field?.type === FIELDS_TYPES.INLINE_CHECK ||
    field?.type === FIELDS_TYPES.VERTICAL_CHECK
  ) {
    return getCheckboxValue({
      value: field?.value,
      defaultValue: field?.default,
    });
  }

  if (field?.type === FIELDS_TYPES.NUMBER) {
    return field?.value ?? field?.default;
  }

  return field?.value;
};

export const initModuleList = ({ fields }) =>
  fields.map((field) => ({
    id: field.id || `${field.field_id}_1`,
    field_id: field.field_id,
    required: field.required,
    value:
      field?.value ||
      field?.data ||
      JSON.parse(field?.options || {})?.values[0]?.val ||
      "",
  }));

export const getDefaultValue = ({ values, id }) =>
  _.chain(values)
    .filter((elm) => elm?.id === id)
    .first()
    .get("value")
    .value();

export const tableInitialValues = ({ data, initialRow, defaultRow }) => {
  const tableLinesNumber = _.chain(data)
    .map((elm) => _.last(elm.id))
    .map(_.toNumber)
    .max()
    .value();
  return _.times(tableLinesNumber, (n) =>
    Object.keys(initialRow).reduce(
      (prev, curr) => ({
        ...prev,
        [curr]: _.chain(data)
          .filter((elm) => elm.id === `${curr}-${n + 1}`)
          .first()
          .get("value", defaultRow[curr])
          .value(),
        id: n + 1,
      }),
      {}
    )
  );
};

export const getBalancesheetFields = (flattenTree) =>
  _.chain(flattenTree)
    .map("fields")
    .flatten()
    .filter((elm) => !_.isNil(elm))
    .value();

export const verifyMinLength = ({ options, value }) =>
  _.isEmpty(options) || _.size(value) > options.min_length;

export const getFieldOptions = ({ fields, fieldId }) => {
  const options = _.chain(fields)
    .filter((elm) => elm?.field_id === fieldId)
    .first()
    .get("options", "[]")
    .value();
  return JSON.parse(options);
};

const findFieldIndex = (form, val) =>
  _.findIndex(
    form?.fields,
    (elm) => elm?.id === val?.id || elm?.field_id === val?.field_id
  );

const handleNewField = (old, val) => ({
  ...old,
  fields: !_.isNil(old?.fields)
    ? [...old.fields, { id: `${val?.field_id}_1`, ...val }]
    : [{ id: `${val?.field_id}_1`, ...val }],
});

const handleICModuleField = (form, val) => {
  const updatedField = {
    id: val?.field_id,
    value: val?.value,
    field_id: _.chain(val?.field_id).slice(0, 2).toNumber().value(),
  };
  const newFields = _.isNil(form?.fields)
    ? [updatedField]
    : form?.fields?.map((elm) =>
        elm?.id === val?.field_id ? updatedField : elm
      );
  return { ...form, fields: newFields };
};

const handleMissionModuleField = (form, val, missionId) => {
  const updatedField = {
    id: val?.id,
    value: val?.value,
    field_id: val?.field_id,
    mission_id: missionId || "",
  };
  const newFields = _.isNil(form?.fields)
    ? [updatedField]
    : form?.fields?.map((elm) => (elm?.id === val?.id ? updatedField : elm));
  return { ...form, fields: newFields };
};

const handleOtherField = (old, val, indexField) => {
  return {
    ...old,
    fields: old.fields.map((elm, index) =>
      index === indexField ? { ...elm, ...val, id: `${val?.field_id}_1` } : elm
    ),
  };
};

export const onChangeStructure = ({ val, setForm, form, missionId }) => {
  const indexField = findFieldIndex(form, val);

  if (indexField === -1) {
    setForm((old) => handleNewField(old, val));
  } else if (_.endsWith(val?.field_id, "IC")) {
    setForm(() => handleICModuleField(form, val));
  } else if (_.chain(val?.id).split("_").size().value() === 2) {
    setForm(() => handleMissionModuleField(form, val, missionId));
  } else {
    setForm((old) => handleOtherField(old, val, indexField));
  }
};

export const formatEvaluationMissionPayload = (
  data,
  action,
  { processedBy } = {}
) => ({
  action: action,
  fields: _.chain(data)
    .map((elm) => ({
      id: elm?.id,
      value: formatFieldValueForApi(elm?.value),
    }))
    .value(),
  page: null,
  processed_by: processedBy,
});

export const isBlankFieldValue = (value) =>
  value == null || (typeof value === "string" && value.trim() === "");

export const isContributionDescriptionField = (field) => {
  const label = String(field?.label || "").trim();

  return (
    field?.type === FIELDS_TYPES.TEXT_AREA &&
    label.startsWith("Description de la") &&
    label.includes("30 mots minimum")
  );
};

export const shouldOmitBlankDescriptionField = ({
  currentValue,
  initialField,
}) =>
  isContributionDescriptionField(initialField) &&
  isBlankFieldValue(currentValue);

const formatFieldValueForApi = (value) => {
  if (_.isNil(value)) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

export const formatAnnualBalancesheetPayload = (
  data,
  action,
  { processedBy, salary_validation, balancesheetFields = [] } = {}
) => {
  const salaryPercentageFieldIds = new Set(
    balancesheetFields.map(getSalaryPercentageFieldId).filter(Boolean)
  );

  return {
    action,
    fields: _.chain(data)
      .map((elm) => ({
        id: elm?.id,
        value: normalizeAnnualBalancesheetValueForApi({
          fieldId: elm?.fieldId,
          value: elm?.value,
          salaryPercentageFieldIds,
        }),
      }))
      .value(),
    page: null,
    processed_by: processedBy,
    salary_validation: salary_validation,
  };
};

//getFieldError returns the field’s error (by id) from a single error object or an errors map
//otherwise returns null
export const getFieldError = ({ errors, id }) => {
  if (!errors) return null;
  if (typeof errors === "object") {
    if (errors[id]) return errors[id];
    return null;
  }
  if (Array.isArray(errors)) {
    return errors.find((e) => e?.ref?.name === id || e?.id === id) || null;
  }

  return null;
};

export const formatData = (data) => {
  //group fields by lines
  const groupedData = _.chain(data)
    .filter((elm) => elm?.id)
    .groupBy((item) => {
      const idParts = item?.id?.split("_");
      return idParts[idParts?.length - 1];
    })
    .value();
  const dataList = Object.values(groupedData);
  //format rows
  if (dataList?.length > 0) {
    const transformedList = dataList.map((group, index) => {
      const transformedGroup = {
        id: index + 1,
      };
      group.forEach((item) => {
        if (item?.id) {
          const idParts = item?.id?.split("_");
          const idWithoutTermination = idParts[0];
          transformedGroup[idWithoutTermination] = item.value;
        }
      });
      return transformedGroup;
    });
    return transformedList;
  }
  return [];
};

//this function below takes a field ID and splits it into a base name and a line number
//exp : parseFieldIdAndLine("1915_2") => { base: "1915", lineId: 2 }
export const parseFieldIdAndLine = (id) => {
  const obj = String(id || "").match(/^(.*)_(\d+)$/);
  return obj
    ? { base: obj[1], lineId: Number(obj[2]) }
    : { base: String(id || ""), lineId: 1 };
};

// A field's "line" is carried by its own id (`<field_id>_<line>`), where the line
// is either a table row number or the id of the mission the field belongs to.
// Dynamic TABLE modules are the exception: their lines are driven by the rows state.
// Validation MUST key its errors exactly like the renderers do, otherwise an error
// is raised but never displayed and the page silently refuses to move forward.
export const getFieldLineIds = (field, moduleRows) => {
  if (moduleRows) return moduleRows.map((row) => row?.id).filter(Boolean);

  return [parseFieldIdAndLine(field?.id).lineId];
};

// getFieldFromModules searches through the given modules and returns the first field whose field_id matches baseId; if none is found, it returns null
export const getFieldFromModules = (baseId, filteredModules = []) =>
  filteredModules
    .flatMap((m) => m?.fields ?? [])
    .find((f) => f?.field_id === baseId) ?? null;

export const isTextField = (t) =>
  t === FIELDS_TYPES.TEXT || t === FIELDS_TYPES.TEXT_AREA;

export const isNumberField = (t) => t === FIELDS_TYPES.NUMBER;

export const isDateField = (t) => t === FIELDS_TYPES.DATE;

export const getMinLength = (field) => {
  const opt = field?.options ? JSON.parse(field.options) : {};
  return Number(opt["min-length"] || 0);
};

export const countWords = (s) =>
  String(s || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

export const createError = (type, message, id) => ({
  type,
  message,
  ref: { name: id },
});

export const transformErrors = (errorsArray) => {
  if (!errorsArray || !Array.isArray(errorsArray)) return {};
  const missionsErrors = {};

  const normalizedErrorCodes = {
    REQUIRED_FIELD: "required",
    REQUIRED: "required",
    MIN_LENGTH: "min_mots",
    MIN_WORDS: "min_mots",
  };

  errorsArray.forEach((error) => {
    if (error?.field_id) {
      const rawFieldId = String(error.field_id);
      const id = /_\d+$/.test(rawFieldId) ? rawFieldId : `${rawFieldId}_1`;
      const errorCode =
        normalizedErrorCodes[error?.error] || error?.error || "required";
      missionsErrors[id] = createError(
        errorCode,
        errorCode,
        id
      );
    }
  });
  return missionsErrors;
};

const getInputValue = (value) => {
  const fromEvent = value?.target?.value;
  return fromEvent !== undefined ? fromEvent : value;
};

const isEmpty = (value) =>
  value == null || (typeof value === "string" && value.trim() === "");

export const validateField = (field, value, id) => {
  if (isTextField(field?.type)) {
    const minLength = getMinLength(field);
    const wordsCount = countWords(value);
    if (field?.required && wordsCount === 0)
      return createError("required", "required", id);
    if (minLength > 0 && wordsCount < minLength)
      return createError("minLength", "min_mots", id);
    return null;
  }

  if (isNumberField(field?.type) || isDateField(field?.type)) {
    const v = getInputValue(value);
    if (field?.required && isEmpty(v)) {
      return createError("required", "required", id);
    }
    return null;
  }
  return null;
};
